/**
 * Anomaly detector for test results (bonus A — "Bad-bottle warning").
 *
 * Pure stats helper: compares a new numeric result against the trailing
 * 7-day sample mean and standard deviation for the same parameter.  Flags
 * results that fall more than 3σ away.
 */
const EPSILON = 1e-9;
/**
 * Detect whether `newResult` is a statistical outlier relative to recent
 * history for the same parameter.
 *
 * @param parameterKey  The parameter being tested.
 * @param newResult     The numeric result the user just obtained.
 * @param priorLogs     All existing test-log entries (will be filtered).
 * @param options       Optional window and threshold overrides.
 */
export function detectAnomalousResult(parameterKey, newResult, priorLogs, options) {
    const windowDays = options?.windowDays ?? 7;
    const thresholdSigma = options?.thresholdSigma ?? 3;
    const now = Date.now();
    const windowMs = windowDays * 24 * 60 * 60 * 1000;
    // Filter prior logs: same parameterKey, numeric result, within window
    const recentValues = priorLogs
        .filter((entry) => entry.parameterKey === parameterKey &&
        typeof entry.result === 'number' &&
        now - new Date(entry.date).getTime() <= windowMs)
        .map((entry) => entry.result);
    // Need at least 3 prior data points for meaningful stats
    if (recentValues.length < 3) {
        return {
            isAnomalous: false,
            mean: null,
            stdDev: null,
            deltaSigma: null,
            reason: 'insufficient-history',
        };
    }
    const n = recentValues.length;
    const sum = recentValues.reduce((acc, v) => acc + v, 0);
    const mean = sum / n;
    // Sample standard deviation (n-1 denominator)
    const sumSquaredDiffs = recentValues.reduce((acc, v) => acc + (v - mean) * (v - mean), 0);
    const variance = sumSquaredDiffs / (n - 1);
    // Zero-variance case: all prior values identical
    if (variance < EPSILON) {
        const diff = Math.abs(newResult - mean);
        if (diff > EPSILON) {
            return {
                isAnomalous: true,
                mean,
                stdDev: 0,
                deltaSigma: Infinity,
                reason: 'zero-variance-mismatch',
            };
        }
        return {
            isAnomalous: false,
            mean,
            stdDev: 0,
            deltaSigma: 0,
            reason: 'within-threshold',
        };
    }
    const stdDev = Math.sqrt(variance);
    const deltaSigma = Math.abs(newResult - mean) / stdDev;
    if (deltaSigma > thresholdSigma) {
        return {
            isAnomalous: true,
            mean,
            stdDev,
            deltaSigma,
            reason: 'beyond-threshold',
        };
    }
    return {
        isAnomalous: false,
        mean,
        stdDev,
        deltaSigma,
        reason: 'within-threshold',
    };
}
//# sourceMappingURL=bad-bottle-detector.js.map